import type { TypographyProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";

const parseFontSizePx = (
  fontSize: string | number | undefined,
  theme: Theme
) => {
  if (typeof fontSize === "number") {
    return fontSize;
  }

  if (typeof fontSize === "string") {
    if (fontSize.endsWith("px")) {
      return parseFloat(fontSize);
    }

    if (fontSize.endsWith("rem")) {
      return parseFloat(fontSize) * 16;
    }
  }

  const captionSize = theme.typography.caption.fontSize;
  return typeof captionSize === "number"
    ? captionSize
    : parseFloat(String(captionSize));
};

const parseLineHeightPx = (
  lineHeight: string | number | undefined,
  fontSizePx: number
) => {
  if (typeof lineHeight === "number") {
    return fontSizePx * lineHeight;
  }

  if (typeof lineHeight === "string") {
    if (lineHeight.endsWith("%")) {
      return fontSizePx * (parseFloat(lineHeight) / 100);
    }

    if (lineHeight.endsWith("px")) {
      return parseFloat(lineHeight);
    }

    const numeric = parseFloat(lineHeight);
    if (!Number.isNaN(numeric)) {
      return fontSizePx * numeric;
    }
  }

  return fontSizePx * 1.5;
};

export const getCollapsedMaxHeightPx = (
  theme: Theme,
  variant: TypographyProps["variant"],
  maxCollapsedLine: number
) => {
  const typographyVariant =
    typeof variant === "string" && variant in theme.typography
      ? theme.typography[variant as keyof typeof theme.typography]
      : theme.typography.caption;

  const fontSizePx = parseFontSizePx(
    typeof typographyVariant === "object" && "fontSize" in typographyVariant
      ? typographyVariant.fontSize
      : undefined,
    theme
  );

  const lineHeightPx = parseLineHeightPx(
    typeof typographyVariant === "object" && "lineHeight" in typographyVariant
      ? typographyVariant.lineHeight
      : undefined,
    fontSizePx
  );

  return maxCollapsedLine * lineHeightPx;
};
